/**
 * Regroupement des pixels chauds en foyers.
 *
 * Un grand incendie n'est pas une collection de petits feux. Le massif landais
 * apparaissait en douze zones distinctes, Cestas, Lanton, Arès, Le Temple,
 * Biscarrosse, Le Porge, alors qu'il s'agit d'un seul sinistre. La cause était
 * le regroupement : glouton, et fondé sur le barycentre courant de l'amas.
 * Plus l'amas grossit, plus son centre s'éloigne des nouveaux pixels, qui
 * finissent donc par ouvrir un foyer de plus.
 *
 * On regroupe désormais par LIAISON SIMPLE : deux pixels à moins de 6 km sont
 * dans le même foyer, et la relation se propage de proche en proche. Un front
 * de flammes continu forme donc une seule zone, quelle que soit sa longueur,
 * tandis que deux incendies réellement séparés restent distincts. Le résultat
 * ne dépend plus de l'ordre d'arrivée des pixels.
 */
import {
  FOOTPRINT_DEFAULT_KM,
  FOOTPRINT_GRID_KM,
  MAX_ZONES,
  WEAK_CONFIDENCE,
  WEAK_DETECTION_PIXELS,
  WEAK_FRP_MW,
  ZONE_LINK_KM,
  ZONE_MAX_OUTLINE_RINGS,
  ZONE_MIN_RADIUS_KM,
  ZONE_RADIUS_MARGIN_KM,
} from './constants';
import { distanceKm, footprintOutlines, isInsideRings } from './geo';
import { STATIC_SOURCE_BY_CELL, type StaticThermalSource } from './static-sources';
import type { FireDetection, FireZone, LatLon, Place, Ring } from './types';

/** Un vrai événement : soit aucune source permanente, soit une anomalie. */
export function isRealFire(zone: FireZone): boolean {
  return zone.staticSource === null || zone.anomalous;
}

/**
 * Identifiant stable dérivé de la position, arrondie au centième de degré.
 *
 * Il ne change pas d'un balayage à l'autre, ce qui permet de garder la
 * sélection de l'utilisateur et le nom de commune déjà résolu.
 */
export function zoneId(lat: number, lon: number): string {
  return `${lat.toFixed(2)},${lon.toFixed(2)}`;
}

/*
 * Pas de la grille spatiale, en degrés.
 *
 * Il doit valoir AU MOINS la distance de liaison en kilomètres, sinon un
 * voisinage de 3 par 3 cases ne suffirait plus à trouver tous les candidats.
 * Pour la longitude on prend la valeur la plus défavorable de France
 * métropolitaine, celle de Dunkerque, où un degré ne fait plus que 69 km.
 */
const KM_PER_DEGREE_LAT = 110.6;
const KM_PER_DEGREE_LON_MIN = 69.4;

/** Liaison simple par union-find, avec grille spatiale pour éviter le quadratique. */
function clusterPoints(
  points: readonly FireDetection[],
  linkKm: number,
): FireDetection[][] {
  const count = points.length;
  if (count === 0) return [];

  const parent = new Int32Array(count);
  for (let i = 0; i < count; i += 1) parent[i] = i;

  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root]!;
    // Compression de chemin : les recherches suivantes sont immédiates.
    let walk = index;
    while (parent[walk] !== root) {
      const next = parent[walk]!;
      parent[walk] = root;
      walk = next;
    }
    return root;
  };

  const union = (a: number, b: number): void => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent[rootB] = rootA;
  };

  const latStep = linkKm / KM_PER_DEGREE_LAT;
  const lonStep = linkKm / KM_PER_DEGREE_LON_MIN;
  const cellOf = (point: LatLon): [number, number] => [
    Math.floor(point.lat / latStep),
    Math.floor(point.lon / lonStep),
  ];

  const buckets = new Map<string, number[]>();
  points.forEach((point, index) => {
    const [row, col] = cellOf(point);
    const key = `${row},${col}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(index);
    else buckets.set(key, [index]);
  });

  for (let index = 0; index < count; index += 1) {
    const point = points[index]!;
    const [row, col] = cellOf(point);
    for (let dRow = -1; dRow <= 1; dRow += 1) {
      for (let dCol = -1; dCol <= 1; dCol += 1) {
        const neighbours = buckets.get(`${row + dRow},${col + dCol}`);
        if (!neighbours) continue;
        for (const other of neighbours) {
          if (other <= index) continue;
          const candidate = points[other]!;
          if (distanceKm(point.lat, point.lon, candidate.lat, candidate.lon) < linkKm) {
            union(index, other);
          }
        }
      }
    }
  }

  const groups = new Map<number, FireDetection[]>();
  for (let index = 0; index < count; index += 1) {
    const root = find(index);
    const group = groups.get(root);
    if (group) group.push(points[index]!);
    else groups.set(root, [points[index]!]);
  }
  return [...groups.values()];
}

/**
 * Proportion de cellules déjà connues comme permanentes au-delà de laquelle la
 * zone est elle-même considérée comme une source industrielle.
 */
const STATIC_CELL_RATIO = 0.6;

/**
 * Au-delà de ce rayon, l'empreinte dépasse celle d'une torchère.
 *
 * C'est le garde-fou qui permet à un incendie DE raffinerie de rester une
 * alerte : sa fumée est bien plus toxique que celle d'une pinède.
 */
const STATIC_MAX_RADIUS_KM = 6;

interface StaticClassification {
  source: StaticThermalSource | null;
  anomalous: boolean;
}

function classifyStatic(points: readonly LatLon[], radiusKm: number): StaticClassification {
  const cells = new Set(points.map((point) => zoneId(point.lat, point.lon)));

  let onStatic = 0;
  let match: StaticThermalSource | null = null;
  for (const cell of cells) {
    const source = STATIC_SOURCE_BY_CELL.get(cell);
    if (!source) continue;
    onStatic += 1;
    if (!match || source.daysSeen > match.daysSeen) match = source;
  }

  if (match === null) return { source: null, anomalous: false };

  const ratio = cells.size > 0 ? onStatic / cells.size : 0;
  if (ratio < STATIC_CELL_RATIO) {
    // Trop de cellules inconnues : quelque chose brûle en plus de l'usine.
    return { source: null, anomalous: false };
  }

  return { source: match, anomalous: radiusKm > STATIC_MAX_RADIUS_KM };
}

/**
 * Regroupe les détections en foyers, puis en décrit le contour.
 *
 * Le satellite voit toute l'Europe : on ne garde que ce qui tombe dans le
 * contour de la France, sinon la carte se couvre de feux belges et italiens.
 *
 * @param knownPlaces noms de communes déjà résolus, pour ne pas les reperdre.
 */
export function groupIntoZones(
  points: readonly FireDetection[],
  rings: readonly Ring[],
  knownPlaces?: ReadonlyMap<string, Place>,
): FireZone[] {
  const inFrance = points.filter((point) => isInsideRings(point.lat, point.lon, rings));

  return clusterPoints(inFrance, ZONE_LINK_KM)
    .map((cluster): FireZone => {
      let sumLat = 0;
      let sumLon = 0;
      for (const point of cluster) {
        sumLat += point.lat;
        sumLon += point.lon;
      }
      const lat = sumLat / cluster.length;
      const lon = sumLon / cluster.length;

      const spread = Math.max(
        ...cluster.map((point) => distanceKm(lat, lon, point.lat, point.lon)),
      );
      const id = zoneId(lat, lon);
      const radiusKm = Math.max(ZONE_MIN_RADIUS_KM, spread + ZONE_RADIUS_MARGIN_KM);
      const classified = classifyStatic(cluster, radiusKm);

      /*
       * On retient le MAXIMUM, pas la moyenne. Un groupe contenant une seule
       * detection a haute confiance est un incendie, meme entoure de detections
       * douteuses. Moyenner diluerait justement le signal qui compte.
       */
      const confidences = cluster.map((point) => point.confidence).filter(isNumber);
      const powers = cluster.map((point) => point.frp).filter(isNumber);
      const times = cluster.map((point) => point.at).filter(isNumber);

      return {
        id,
        lat,
        lon,
        pixelCount: cluster.length,
        confidence: confidences.length > 0 ? Math.max(...confidences) : null,
        maxFrp: powers.length > 0 ? Math.max(...powers) : null,
        observedAt: times.length > 0 ? Math.max(...times) : null,
        rings: footprintOutlines(
          cluster,
          FOOTPRINT_GRID_KM,
          FOOTPRINT_DEFAULT_KM,
          ZONE_MAX_OUTLINE_RINGS,
        ),
        radiusKm,
        place: knownPlaces?.get(id) ?? null,
        staticSource: classified.source,
        anomalous: classified.anomalous,
      };
    })
    // Les foyers les plus étendus d'abord : ce sont eux qui comptent.
    .sort((a, b) => b.pixelCount - a.pixelCount)
    .slice(0, MAX_ZONES);
}

/**
 * Le point de la zone le plus proche d'un endroit donné.
 *
 * Indispensable dès qu'un foyer est vaste. Pour l'incendie du massif landais,
 * le barycentre peut se trouver à quarante kilomètres alors que le front de
 * flammes est à cinq : calculer une durée de trajet depuis le centre donnait
 * un résultat huit fois trop long.
 *
 * On parcourt les sommets de tous les contours, qui bornent la zone. À défaut
 * de contour, il ne reste que le centre.
 *
 * Ces sommets sont des coins de cellule, non des centres de détection. Une
 * cellule étant marquée dès qu'elle rencontre l'empreinte, son coin peut se
 * trouver jusqu'à 0,80 km plus PRÈS que la détection elle-même pour VIIRS. Le
 * chiffre est borné et vérifié dans tests/outline.test.ts, et l'écart va dans
 * le sens de la prudence, seule direction acceptable ici.
 */
export function nearestPointOf(zone: FireZone, lat: number, lon: number): LatLon {
  let best = { lat: zone.lat, lon: zone.lon };
  let bestDistance = distanceKm(lat, lon, zone.lat, zone.lon);

  for (const ring of zone.rings) {
    for (const vertex of ring) {
      const candidate = distanceKm(lat, lon, vertex[0], vertex[1]);
      if (candidate < bestDistance) {
        bestDistance = candidate;
        best = { lat: vertex[0], lon: vertex[1] };
      }
    }
  }
  return best;
}

const isNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * La detection est-elle trop faible pour etre presentee comme un incendie ?
 *
 * Avec FIRMS, la confiance et la puissance radiative tranchent. Sans elle, le
 * nombre de taches est le seul substitut : l'incendie du massif landais en
 * donnait 642, une detection isolee pres de Blanquefort en donnait 8.
 */
export function isWeakDetection(zone: FireZone): boolean {
  if (zone.confidence !== null) {
    if (zone.confidence < WEAK_CONFIDENCE) return true;
    return zone.maxFrp !== null && zone.maxFrp < WEAK_FRP_MW;
  }
  return zone.pixelCount < WEAK_DETECTION_PIXELS;
}
